// Chooses between the new UI and the classic (V2) UI.
// Only one UI bundle is loaded per page load (see index.jsx) because their
// global stylesheets conflict, so switching saves the choice and reloads.

export const UI_VERSION_KEY = "ui-version";

export const UI_VERSIONS = {
  NEW: "new",
  CLASSIC: "classic",
};

// Modules whose main table page is the same route in both UIs. Switching from
// any page of a module (add / edit / detail) lands on that module's main page.
const SHARED_MODULE_PATHS = [
  "/farmer",
  "/suppliers",
  "/molasses",
  "/fermentation",
  "/strippingprocesslist",
  "/spiritprocesslist",
  "/proofing",
  "/caskmaster",
  "/caskandaging",
  "/bottling",
];

// Single pages that exist in both UIs (exact path -> path in the other UI).
// "/profile" is a new-UI alias of "/user/edit", which both UIs have.
const SHARED_SINGLE_PAGES = {
  "/user/edit": "/user/edit",
  "/profile": "/user/edit",
  "/changepassword": "/changepassword",
  "/ai-assistant": "/ai-assistant",
};

// Pages shown before sign-in; switching keeps the user on the same page.
const PUBLIC_PATHS = [
  "/login",
  "/forgotpassword",
  "/checkemail",
  "/resetpassword",
  "/bottle-lookup",
];

// Where switching lands from any page that is not in a shared module.
const SWITCH_FALLBACK_PATH = "/dashboard";

const isValidVersion = (value) =>
  Object.values(UI_VERSIONS).includes(value);

const readStored = () => {
  try {
    return localStorage.getItem(UI_VERSION_KEY);
  } catch {
    return null;
  }
};

const writeStored = (version) => {
  try {
    localStorage.setItem(UI_VERSION_KEY, version);
  } catch {
    // storage unavailable: choice lasts for this page load only
  }
};

// A `?ui=classic` / `?ui=new` query param overrides and persists the choice.
export const getUIVersion = () => {
  const fromQuery = new URLSearchParams(window.location.search).get("ui");
  if (isValidVersion(fromQuery)) {
    writeStored(fromQuery);
    return fromQuery;
  }
  const stored = readStored();
  return isValidVersion(stored) ? stored : UI_VERSIONS.NEW;
};

export const isClassicUI = () => getUIVersion() === UI_VERSIONS.CLASSIC;

const findPath = (pathname, paths) =>
  paths.find((p) => pathname === p || pathname.startsWith(`${p}/`));

// Page to land on in the other UI when switching from `pathname`.
const getSwitchTargetPath = (pathname) => {
  const path = pathname.toLowerCase();
  if (findPath(path, PUBLIC_PATHS)) return pathname;
  const singlePage = SHARED_SINGLE_PAGES[path.replace(/\/+$/, "")];
  return (
    singlePage || findPath(path, SHARED_MODULE_PATHS) || SWITCH_FALLBACK_PATH
  );
};

export const switchUIVersion = (version) => {
  if (!isValidVersion(version)) return;
  writeStored(version);

  const { pathname, hash } = window.location;
  const targetPath = getSwitchTargetPath(pathname);

  // Query/hash belong to the page being left, so keep them only when staying on it.
  const params = new URLSearchParams(window.location.search);
  params.delete("ui");
  const search = params.toString() ? `?${params.toString()}` : "";
  const targetUrl =
    targetPath === pathname ? `${pathname}${search}${hash}` : targetPath;

  if (window.location.pathname !== targetPath || window.location.search.includes("ui=")) {
    window.location.href = targetUrl;
  } else {
    window.location.reload();
  }
};

export const toggleUIVersion = () => {
  const current = getUIVersion();
  const next = current === UI_VERSIONS.CLASSIC ? UI_VERSIONS.NEW : UI_VERSIONS.CLASSIC;
  switchUIVersion(next);
};
